import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Unlock,
  Search,
  Ban,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const EVENT_LABELS = {
  payment_failed: { label: "Pago fallido", color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  stars_payment_failed: { label: "Stars rechazado", color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  dispute_opened: { label: "Disputa Stripe", color: "text-red-400 bg-red-500/10 border-red-500/30" },
  chargeback: { label: "Contracargo", color: "text-red-500 bg-red-500/15 border-red-500/40" },
  rapid_failed_attempts: { label: "Múltiples fallos", color: "text-orange-400 bg-orange-500/10 border-orange-500/30" },
  manual_admin: { label: "Manual admin", color: "text-zinc-300 bg-zinc-500/10 border-zinc-500/30" },
};

function EventBadge({ event }) {
  const cfg = EVENT_LABELS[event] || {
    label: event,
    color: "text-zinc-300 bg-zinc-500/10 border-zinc-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border whitespace-nowrap ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
}

function StatusPill({ status }) {
  if (status === "banned") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border text-red-400 bg-red-500/10 border-red-500/30">
        <Ban size={11} strokeWidth={2.5} />
        BANEADO
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border text-emerald-400 bg-emerald-500/10 border-emerald-500/30">
      <ShieldCheck size={11} strokeWidth={2.5} />
      ACTIVO
    </span>
  );
}

export default function FraudEvents() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?telegram_id=${encodeURIComponent(filter)}` : "";
      const [eventsR, sumR] = await Promise.all([
        api.get(`/fraud-events${params}`),
        api.get("/fraud-events/summary"),
      ]);
      setItems(eventsR.data.items || []);
      setSummary(sumR.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando eventos");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh every 15s
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const unban = async (telegramId) => {
    if (!window.confirm(`¿Desbloquear al usuario ${telegramId}? Se reinicia el fraud_score a 0.`)) {
      return;
    }
    setBusy((b) => ({ ...b, [telegramId]: true }));
    try {
      await api.post(`/users/${telegramId}/unban`);
      toast.success(`Usuario ${telegramId} reactivado`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error desbloqueando");
    } finally {
      setBusy((b) => ({ ...b, [telegramId]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="fraud-events-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-2">
            <ShieldAlert className="text-red-400" size={26} strokeWidth={2.5} />
            Anti-fraude
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Eventos sospechosos: pagos fallidos, disputas y contracargos. Auto-bloqueo en score ≥ 10.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          className="border-zinc-800 hover:bg-zinc-900 rounded-sm"
          data-testid="fraud-refresh-btn"
          disabled={loading}
        >
          <RefreshCw size={13} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <div className="text-xs text-zinc-500 mb-1">Eventos totales</div>
          <div className="text-2xl font-mono font-black">
            {summary?.events_total ?? "—"}
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <div className="text-xs text-zinc-500 mb-1">Usuarios baneados</div>
          <div className="text-2xl font-mono font-black text-red-400">
            {summary?.banned_count ?? "—"}
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <div className="text-xs text-zinc-500 mb-1">Top sospechoso (score)</div>
          <div className="text-2xl font-mono font-black text-amber-400">
            {summary?.top_users?.[0]?.score_total ?? "—"}
          </div>
        </div>
      </div>

      {/* Top sospechosos */}
      {summary?.top_users?.length > 0 && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-900 flex items-center gap-2">
            <TrendingUp size={14} className="text-amber-400" />
            <div className="text-xs font-mono font-bold uppercase tracking-wider text-zinc-400">
              Top usuarios sospechosos
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-4 py-2">Usuario</th>
                  <th className="text-left px-4 py-2">Telegram ID</th>
                  <th className="text-left px-4 py-2">Estado</th>
                  <th className="text-right px-4 py-2">Score</th>
                  <th className="text-right px-4 py-2">Eventos</th>
                  <th className="text-left px-4 py-2">Tipos</th>
                  <th className="text-right px-4 py-2">Acción</th>
                </tr>
              </thead>
              <tbody>
                {summary.top_users.map((u) => {
                  const types = Array.from(new Set(u.events_by_type || []));
                  return (
                    <tr
                      key={u.telegram_id}
                      className="border-t border-zinc-900 hover:bg-zinc-900/30"
                      data-testid={`fraud-top-row-${u.telegram_id}`}
                    >
                      <td className="px-4 py-2.5 font-bold">
                        @{u.username || "(sin user)"}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">
                        {u.telegram_id}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusPill status={u.status} />
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold text-amber-400">
                        {u.score_total}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-zinc-400">
                        {u.events_total}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-1 flex-wrap">
                          {types.slice(0, 3).map((t) => (
                            <EventBadge key={t} event={t} />
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {u.status === "banned" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => unban(u.telegram_id)}
                            disabled={busy[u.telegram_id]}
                            className="rounded-sm border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 h-7 text-xs"
                            data-testid={`fraud-unban-${u.telegram_id}`}
                          >
                            <Unlock size={11} className="mr-1" />
                            Desbloquear
                          </Button>
                        ) : (
                          <span className="text-[10px] text-zinc-600 font-mono">
                            Vigilando
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filter + events */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-900 flex items-center gap-2 flex-wrap">
          <AlertTriangle size={14} className="text-zinc-400" />
          <div className="text-xs font-mono font-bold uppercase tracking-wider text-zinc-400">
            Eventos recientes
          </div>
          <div className="flex-1" />
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              placeholder="Filtrar por Telegram ID"
              value={filter}
              onChange={(e) => setFilter(e.target.value.replace(/\D/g, ""))}
              className="bg-zinc-900 border-zinc-800 rounded-sm h-8 pl-7 text-xs w-44"
              data-testid="fraud-filter-input"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              <tr>
                <th className="text-left px-4 py-2">Fecha</th>
                <th className="text-left px-4 py-2">Usuario</th>
                <th className="text-left px-4 py-2">Evento</th>
                <th className="text-right px-4 py-2">Peso</th>
                <th className="text-left px-4 py-2">Detalle</th>
                <th className="text-left px-4 py-2">Estado actual</th>
                <th className="text-right px-4 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-zinc-600 text-xs">
                    Sin eventos {filter ? `para ${filter}` : "registrados"}.
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="border-t border-zinc-900 hover:bg-zinc-900/30"
                  data-testid={`fraud-row-${it.id}`}
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-500 whitespace-nowrap">
                    {new Date(it.created_at).toLocaleString("es-MX", {
                      year: "2-digit",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-bold">
                      @{it.username || "(sin user)"}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500">
                      {it.telegram_id}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <EventBadge event={it.event} />
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold">
                    +{it.score}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-400 max-w-[260px] truncate">
                    {it.detail || "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusPill status={it.user_status} />
                    <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                      score: {it.user_fraud_score}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {it.user_status === "banned" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => unban(it.telegram_id)}
                        disabled={busy[it.telegram_id]}
                        className="rounded-sm border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 h-7 text-xs"
                        data-testid={`fraud-row-unban-${it.id}`}
                      >
                        <Unlock size={11} className="mr-1" />
                        Desbloquear
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
